import { useState } from "react";
import { keyframes, styled } from "styled-components";
import Window from "./common/Window.js";
import Typography from "./common/Typography.js";
import Flow from "./common/Flow.js";
import Button from "./common/Button.js";

const factions = ['COSMIC', 'VOID', 'GALACTIC', 'QUANTUM', 'DOMINION', 'ASTRO', 'CORSAIRS', 'OBSIDIAN', 'AEGIS', 'UNITED', 'SOLITARY', 'COBALT', 'OMEGA', 'ECHO', 'LORDS', 'CULT', 'ANCIENTS', 'SHADOW', 'ETHEREAL'];

const Animation = keyframes`
  0% {
    background-position: 0;
  }

  100% {
    background-position: 100%;
  }
`;

const AnimationTitle = keyframes`
  0% {
    opacity: 0;
    scale: 1.5;
  }


  100% {
    opacity: 1;
    scale: 1;
  }
`;

const Root = styled.div`
  display: grid;
  grid-template-columns: repeat(12, 1fr);
  height: 100%;
`;

const Form = styled.div`
  align-items: center;
  display: flex;
  grid-column: span 5;
  justify-content: center;
  padding-inline: 72px;
`;

const ImageWrapper = styled.div`
  background-color: var(--color-theme-primary-400);
  grid-column: span 7;
  overflow: hidden;
  position: relative;

  &::after,
  &::before {
    --width: 96px;
    border: var(--border-width) var(--border-style) var(--color-theme-secondary-400);
    content: '';
    height: calc(100% - (var(--width) / 2));
    position: absolute;
    top: 50%;
    translate: 0 -50%;
    width: var(--width);
    z-index: 1;
  }

  &::before {
    border-right: 0;
    left: 24px;
  }

  &::after {
    border-left: 0;
    right: 24px;
  }
`;

const Image = styled.div`
  animation-duration: 20s;
  animation-fill-mode: forwards;
  animation-name: ${Animation};
  background-image: url('./images/background.png');
  background-size: cover;
  inset: 0;
  pointer-events: none;
  position: absolute;
  transform-origin: center center;
`;

const Star = styled.div`
  background-image: url('./images/waypoint/types/PLANET_1.png');
  background-position: center center;
  background-repeat: no-repeat;
  background-size: contain;
  inset: 25%;
  position: absolute;
`;

const SpacetradersLogo = styled.a`
  background-image: url('./images/spacetraders-logo.svg');
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
  bottom: var(--space-6);
  height: 60px;
  left: 50%;
  position: absolute;
  translate: -50%;
  width: 60px;
`;

const Title = styled.div`
  animation-delay: 11.25s;
  animation-duration: 2s;
  animation-fill-mode: forwards;
  animation-name: ${AnimationTitle};
  animation-timing-function: ease-in-out;
  color: var(--color-theme-accent-400);
  font-family: var(--font-family-display);
  font-size: clamp(16px, 10vw, 72px);
  font-weight: 700;
  left: 50%;
  line-height: 0.85;
  opacity: 0;
  position: absolute;
  text-align: center;
  top: 50%;
  translate: -50% -50%;
  z-index: 2;

  &::after,
  &::before {
    background-color: var(--color-theme-accent-400);
    content: '';
    height: var(--border-width);
    position: absolute;
    top: 50%;
    width: 96px;
  }

  &::before {
    left: -120px;
  }

  &::after {
    right: -120px;
  }
`;

const LoginWindow = styled(Window)`
  max-width: 576px;
  padding-block: var(--space-12);
  padding-inline: var(--space-6);
  width: 100%;

  a {
    color: var(--color-theme-accent-400);
    display: block;
  }
`;

function Login({ store }) {
  const [name, setName] = useState(null);
  const [faction, setFaction] = useState(null);
  const [token, setToken] = useState(null);
  const [tokenError, setTokenError] = useState("");
  const [nameError, setNameError] = useState("");
  const tab = 'load_game'

  function login() {
    setTokenError(null);
    setNameError(null);

    if (token) {
      store.login(token)
        .then((result) => {
          //store.store(result.token, result.ships, result.system, result.agent, result.contracts);
        })
        .catch((error) => {
          setTokenError(error.message);
        });
    } else if (name && faction) {
      store.register(name, faction)
        .then((result) => {
          //store.store(result.token, result.ships, result.system, result.agent, result.contracts);
        })
        .catch((error) => {
          setNameError(error.message);
        });
    }
  }

  return (
    <Root>
      <ImageWrapper>
        <Image />
        <Star />
        <SpacetradersLogo href="https://spacetraders.io/" target="_blank" />
        <Title>Galaxia<br />Haven</Title>
      </ImageWrapper>
      <Form>
        <LoginWindow>
          <Flow $space={6}>
            {/*<Tabs>
              <TabItem onClick={() => setTab('new_game')} $active={tab === 'new_game'}>New game</TabItem>
              <TabItem onClick={() => setTab('load_game')} $active={tab === 'load_game'}>Load game</TabItem>
            </Tabs>*/}
            {tab === 'new_game' && (
              <>
              <div>
                <Typography as="h1" $lineHeight="body" fontSize="xl">New game</Typography>
                <p>Choose a name and faction to create a new account</p>
              </div>
                <div>
                  <label>Name</label>
                  <input type="text" placeholder="Choose a name" onChange={(e) => setName(e.target.value)} />
                  {nameError && <span className="error">{ nameError }</span>}
                </div>
                <div>
                  <label>Faction</label>
                  <select onChange={(e) => setFaction(e.target.value)} value={faction ?? ''} onKeyDown={(e) => e.key == 'Enter' && login()}>
                    <option>Select faction</option>
                    {factions.map((f) => (
                      <option key={ f }>{ f }</option>
                    ))}
                  </select>
                </div>
                </>
            )}
            {tab === 'load_game' && (
              <>
                <div>
                  <Typography as="h1" $lineHeight="body" fontSize="xl">Start game</Typography>
                  <p>Enter your agent token to start. Note: if you do not have one yet, you can create it at <a href="https://my.spacetraders.io">https://my.spacetraders.io</a></p>
                </div>
                <div>
                  <label>Token</label>
                  <input type="text" onChange={(e) => setToken(e.target.value)} placeholder="Your token" onKeyDown={(e) => e.key == 'Enter' && login()} />
                  {tokenError && <span className="error">{ tokenError }</span>}
                </div>
              </>
            )}
            <Button $action onClick={login}>Start game</Button>
          </Flow>
        </LoginWindow>
      </Form>
    </Root>
  )
}

export default Login;
